import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ModuleContext = createContext(null);

const STORAGE_KEY = 'current_module';
const VALID = ['Admin', 'IT'];

/**
 * ModuleProvider — tracks the active department module (Admin | IT) globally
 * so the top-left header (and any other consumer) reflects the current
 * department slicer the user is interacting with.
 *
 * Persisted in sessionStorage so back-navigation preserves state within the tab.
 * Default: 'Admin' for everyone except IT roles (IT Head / IT Team) who land on IT.
 */
export const ModuleProvider = ({ children, defaultModule = 'Admin' }) => {
  const [currentModule, setModule] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && VALID.includes(stored)) return stored;
    } catch (e) { /* ignore */ }
    return VALID.includes(defaultModule) ? defaultModule : 'Admin';
  });

  // Persist on change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, currentModule);
    } catch (e) { /* ignore */ }
  }, [currentModule]);

  const setCurrentModule = useCallback((m) => {
    if (VALID.includes(m)) setModule(m);
  }, []);

  return (
    <ModuleContext.Provider value={{ currentModule, setCurrentModule }}>
      {children}
    </ModuleContext.Provider>
  );
};

export const useCurrentModule = () => {
  const ctx = useContext(ModuleContext);
  if (!ctx) {
    // Safe fallback so non-wrapped consumers don't crash
    return { currentModule: 'Admin', setCurrentModule: () => {} };
  }
  return ctx;
};
